'use client'

import Loading from '@/components/circle-loading'

import { useState } from 'react'
import Link from 'next/link'
import useSWR from 'swr'
import { ArrowRight } from 'lucide-react'
import CollectionIcon from '@/components/collection-icon'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import useBidReference from '@/utils/hooks/useBidReference'
import { BID_REBATE_PERCENT, offersEnabled } from '@/utils/offers'
import type { BidCollection } from '@/utils/bids'
import OfferDialog from './offer-dialog'
import MakeOfferModal from './make-offer-modal'
import OfferUsd from './offer-usd'
import { bookBtc } from './collection-offer-book'
import styles from './cashback-bar.module.css'

export const CASHBACK_BAR_HEIGHT = 30

/** Whether the site-wide cashback bar is showing; the header sizes its bumper by it. */
export function useCashbackBar() {
	const hydrated = useIsHydrated()
	return { visible: hydrated && offersEnabled() }
}

const fetchBook = async (url: string) => {
	const r = await fetch(url)
	if (!r.ok) throw new Error('Bids unavailable')
	return r.json() as Promise<{ collections: BidCollection[] }>
}

/**
 * The bid cashback, said once at the top of every page: one line, one
 * colour, one action. Clicking the line opens the place-a-bid flow; the ×
 * hides the bar for this browser.
 */
export default function CashbackBar() {
	const [open, setOpen] = useState(false)
	return (
		<>
			<div className={styles.bar} data-testid="cashback-bar">
				<button type="button" className={styles.line} onClick={() => setOpen(true)}>
					<span>
						<span className={styles.limited}>Limited time – </span>
						<strong>{BID_REBATE_PERCENT}% fee rebate</strong>
						<span className={styles.long}> on every winning offer</span>
					</span>
					<span className={styles.cta}>
						Make an offer <ArrowRight size={13} aria-hidden="true" />
					</span>
				</button>
			</div>
			{open && <PlaceBidFlow onClose={() => setOpen(false)} />}
		</>
	)
}

/** The "Floor" figure is the fair-value mark (the floor itself when a collection is unpriced). */
function RowFloor({ slug, floorSats }: { slug: string; floorSats: number | null }) {
	const ref = useBidReference(slug, floorSats)
	return <span>{ref.sats ? <>Floor price {bookBtc(ref.sats)} BTC <OfferUsd sats={ref.sats} /></> : 'No price yet'}</span>
}

/** Top NFT collections; one tap opens the bid form. */
export function PlaceBidFlow({ onClose }: { onClose: () => void }) {
	const { data, error, isLoading } = useSWR('/api/bids/book', fetchBook, { revalidateOnFocus: false })
	// Real collections only: the sub-1k/10k/100k stat wrappers are not things to bid on.
	const collections = (data?.collections || []).filter((c) => !/^sub-?\d/i.test(c.slug)).slice(0, 8)
	const [picked, setPicked] = useState<BidCollection | null>(null)
	if (picked)
		return (
			<MakeOfferModal
				open
				surface="bids_page"
				onClose={() => setPicked(null)}
				collectionSlug={picked.slug}
				initialScope="collection"
				name={picked.name}
				collectionIcon={<CollectionIcon collection={picked as any} />}
				reference={{ floorSats: picked.floor_price ?? null }}
				onPlaced={() => {
					setPicked(null)
					onClose()
				}}
			/>
		)
	return (
		<OfferDialog
			title="Make an offer"
			description={`Pick a collection and name your price. A winning offer pays a lower marketplace fee: up to ${BID_REBATE_PERCENT}% of the offer price comes off it.`}
			onClose={onClose}
			compact
			footer={
				<Link href="/collect" className={styles.browse} onClick={onClose}>
					Browse every collection <ArrowRight size={14} aria-hidden="true" />
				</Link>
			}
		>
			{isLoading ? (
				<div role="status" aria-label="Loading collections" className="flex justify-center py-8">
					<Loading className="!w-6 !h-6" />
				</div>
			) : error || collections.length === 0 ? (
				<p className={styles.status} role="status">
					Top collections are unavailable right now. Open any collection to make an offer.
				</p>
			) : (
				<ol className={styles.list} data-testid="place-bid-collections">
					{collections.map((c) => (
						<li key={c.slug}>
							<button type="button" className={styles.row} onClick={() => setPicked(c)}>
								<span className={styles.icon}>
									<CollectionIcon collection={c as any} className={styles.iconImg} imageClassName={styles.iconImg} />
								</span>
								<span className={styles.copy}>
									<strong>{c.name}</strong>
									<RowFloor slug={c.slug} floorSats={c.floor_price ?? null} />
								</span>
								<span className={styles.bid}>Offer</span>
							</button>
						</li>
					))}
				</ol>
			)}
		</OfferDialog>
	)
}
